import '../styles/components/product-detail.css'
import { getProduct } from '../lib/supabase.js'
import { formatPrice, showToast, imagePlaceholder } from '../utils/helpers.js'
import { store } from '../store.js'
import { navigate } from '../router.js'
import { updateMeta, injectJsonLd, clearJsonLd } from '../utils/seo.js'

export async function renderProduct(outlet, { id }) {
  clearJsonLd()

  outlet.innerHTML = `
    <div class="product-detail-page">
      <div class="container">
        <a href="#/shop" class="back-link">
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
          </svg>
          Back to Shop
        </a>
        <div class="products-loading">
          <div class="spinner"></div>
          <span>Loading product…</span>
        </div>
      </div>
    </div>
  `

  let product
  try {
    product = await getProduct(id)
  } catch (err) {
    console.error('[product] Failed to load:', err)
  }

  if (!product) {
    updateMeta({
      title: 'Product Not Found | Airplaten',
      description: 'This product could not be found.',
      canonicalPath: '/#/shop',
    })
    outlet.querySelector('.product-detail-page .container').innerHTML += `
      <p class="text-secondary">Product not found. <a href="#/shop" class="text-accent">Return to shop →</a></p>
    `
    return
  }

  updateMeta({
    title: `${product.name} | Airplaten Carbon Fiber Platen`,
    description: `${product.description?.slice(0, 150) || product.name}. Carbon fiber platen for belt grinders. Lifetime warranty.`,
    canonicalPath: product.slug ? `/products/${product.slug}` : `/#/product/${product.id}`,
    image: product.images?.[0] || undefined,
  })

  injectJsonLd({
    '@context': 'https://schema.org',
    '@type': 'Product',
    'name': product.name,
    'description': product.description || '',
    'image': product.images?.[0] || 'https://www.airplaten.com/images/airplaten-clip.png',
    'brand': { '@type': 'Brand', 'name': 'Airplaten' },
    'sku': product.sku || '',
    'material': 'Carbon Fiber Composite',
    'offers': {
      '@type': 'Offer',
      'price': (product.price_cents / 100).toFixed(2),
      'priceCurrency': 'CAD',
      'availability': product.stock_qty > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      'url': product.slug ? `https://www.airplaten.com/products/${product.slug}` : `https://www.airplaten.com/#/product/${product.id}`,
      'seller': { '@type': 'Organization', 'name': 'Airplaten' },
      'shippingDetails': {
        '@type': 'OfferShippingDetails',
        'shippingDestination': [
          { '@type': 'DefinedRegion', 'addressCountry': 'CA' },
          { '@type': 'DefinedRegion', 'addressCountry': 'US' }
        ]
      },
      'hasMerchantReturnPolicy': {
        '@type': 'MerchantReturnPolicy',
        'returnPolicyCategory': 'https://schema.org/MerchantReturnFiniteReturnWindow',
        'merchantReturnDays': 30,
        'returnMethod': 'https://schema.org/ReturnByMail'
      }
    }
  }, 'product')

  injectJsonLd({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://www.airplaten.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': 'Shop', 'item': 'https://www.airplaten.com/#/shop' },
      { '@type': 'ListItem', 'position': 3, 'name': product.name }
    ]
  }, 'breadcrumb')

  renderProductDetail(outlet, product)
}

function renderProductDetail(outlet, product) {
  const images = product.images?.length ? product.images : [null]
  let selectedQty = 1
  let selectedImage = 0

  const inStock = product.stock_qty > 0
  const lowStock = product.stock_qty > 0 && product.stock_qty <= 10

  const specRows = product.metadata
    ? Object.entries(product.metadata).map(([k, v]) => `
      <div class="product-spec-row">
        <span class="spec-key">${formatSpecKey(k)}</span>
        <span class="spec-val">${v}</span>
      </div>
    `).join('')
    : ''

  outlet.innerHTML = `
    <div class="product-detail-page">
      <div class="container">
        <a href="#/shop" class="back-link">
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
          </svg>
          Back to Shop
        </a>

        <div class="product-detail-grid">
          <!-- Gallery -->
          <div class="product-gallery">
            <div class="product-main-image">
              <img id="main-img" src="${images[0] || imagePlaceholder(600, 600)}" alt="${escapeHtml(product.name)}">
            </div>
            ${images.length > 1 ? `
              <div class="product-thumbnails">
                ${images.map((img, i) => `
                  <button class="product-thumbnail ${i === 0 ? 'active' : ''}" data-index="${i}">
                    <img src="${img || imagePlaceholder(72, 72)}" alt="View ${i + 1}">
                  </button>
                `).join('')}
              </div>
            ` : ''}
          </div>

          <!-- Info -->
          <div class="product-info">
            <div>
              <div class="product-stock">
                <span class="stock-dot ${!inStock ? 'out' : lowStock ? 'low' : ''}"></span>
                <span class="text-secondary text-sm">
                  ${!inStock ? 'Out of stock' : lowStock ? `Only ${product.stock_qty} left` : 'In stock'}
                </span>
              </div>
            </div>

            <h1 class="product-name">${escapeHtml(product.name)}</h1>

            <div class="product-price-block">
              <span class="product-price">${formatPrice(product.price_cents)}</span>
              ${product.sku ? `<span class="product-sku">SKU: ${escapeHtml(product.sku)}</span>` : ''}
            </div>

            <p class="product-description">${escapeHtml(product.description || '')}</p>

            ${product.purchasable === false ? `
              <a href="${sanitizeUrl(product.button_url)}"
                class="btn btn-secondary product-add-btn"
                ${product.button_url && !product.button_url.startsWith('#') ? 'target="_blank" rel="noopener noreferrer"' : ''}>
                ${escapeHtml(product.button_text || 'Get in touch')}
              </a>
            ` : inStock ? `
              <div class="product-qty-row">
                <span class="product-qty-label">Quantity</span>
                <div class="product-qty-control">
                  <button class="product-qty-btn" id="qty-dec">−</button>
                  <span class="product-qty-value" id="qty-value">1</span>
                  <button class="product-qty-btn" id="qty-inc">+</button>
                </div>
              </div>

              <button class="btn btn-primary product-add-btn" id="add-to-cart-btn">
                Add to Cart — ${formatPrice(product.price_cents)}
              </button>
            ` : `
              <button class="btn btn-secondary product-add-btn" disabled>Sold Out</button>
            `}

            ${specRows ? `
              <div class="product-specs">
                <div class="font-semibold text-sm" style="margin-bottom: var(--space-3); color: var(--color-text-secondary); text-transform: uppercase; font-size: var(--text-xs); letter-spacing: 0.1em;">Specifications</div>
                ${specRows}
              </div>
            ` : ''}
          </div>
        </div>
      </div>
    </div>
  `

  // Thumbnail switching
  outlet.querySelectorAll('.product-thumbnail').forEach(thumb => {
    thumb.addEventListener('click', () => {
      selectedImage = parseInt(thumb.dataset.index)
      const mainImg = document.getElementById('main-img')
      if (mainImg) mainImg.src = images[selectedImage] || imagePlaceholder(600, 600)
      outlet.querySelectorAll('.product-thumbnail').forEach((t, i) => {
        t.classList.toggle('active', i === selectedImage)
      })
    })
  })

  // Qty controls
  const qtyValue = document.getElementById('qty-value')
  const updateAddBtn = () => {
    const btn = document.getElementById('add-to-cart-btn')
    if (btn) btn.textContent = `Add to Cart — ${formatPrice(product.price_cents * selectedQty)}`
  }

  document.getElementById('qty-dec')?.addEventListener('click', () => {
    if (selectedQty > 1) {
      selectedQty--
      if (qtyValue) qtyValue.textContent = selectedQty
      updateAddBtn()
    }
  })

  document.getElementById('qty-inc')?.addEventListener('click', () => {
    if (selectedQty < 99) {
      selectedQty++
      if (qtyValue) qtyValue.textContent = selectedQty
      updateAddBtn()
    }
  })

  // Add to cart
  document.getElementById('add-to-cart-btn')?.addEventListener('click', () => {
    store.addToCart(product, selectedQty)
    store.openCart()
    showToast(`${product.name} added to cart`, 'success')
  })
}

function formatSpecKey(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

function sanitizeUrl(url) {
  if (!url) return '#/contact'
  const s = url.trim()
  if (s.startsWith('#') || s.startsWith('https://') || s.startsWith('http://')) return s
  return '#/contact'
}

function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
